import { HOURS_PER_WORKING_DAY } from '@app/constants/app';
import { toFixedHours } from './date-utils';

/** What one person filed over a stretch of working days, as the shortfall sum needs them. */
type Filed = { id: string; name: string; totalMinutes: number };

/** What one person still owes over that stretch. */
export type Shortfall = { id: string; name: string; missing: number };

/**
 * The hours one person still owes over a stretch of working days: eight for every day of it, minus what they
 * filed. Never negative — hours filed over one day are not credit against another, so somebody at or above
 * the total drops to nothing owed rather than being owed to.
 *
 * The one place the question "is this person behind on reporting" is answered. Insights asks it of a closed
 * sprint to list who owes hours; the warning before a product's sprint is closed asks it of the days elapsed
 * so far. Same question at two moments, so one sum.
 */
export const shortfallOf = (totalMinutes: number, workingDays: number): number =>
  toFixedHours(Math.max(0, workingDays * HOURS_PER_WORKING_DAY - totalMinutes / 60));

/**
 * Who is short of hours over a closed sprint, largest debt first and ties broken by name.
 *
 * Every working day counts, filed or not. Somebody who filed at or above eight hours a day owes nothing and
 * drops out entirely rather than appearing with a negative figure.
 */
export const shortfallsOf = (filed: Filed[], workingDays: number): Shortfall[] =>
  filed
    .map(({ id, name, totalMinutes }) => ({ id, name, missing: shortfallOf(totalMinutes, workingDays) }))
    .filter(person => person.missing > 0)
    .sort((a, b) => b.missing - a.missing || a.name.localeCompare(b.name));
